import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FiArrowLeft, FiInfo, FiAlertTriangle, FiChevronRight, FiClipboard, FiTrash2, FiPause, FiPlay,
  FiShield, FiCheck, FiX, FiRefreshCw, FiSearch, FiActivity,
} from 'react-icons/fi';
import { ROUTES } from '../../app/routes';
import {
  fetchAgencyUsers, fetchRoster, fetchTeamMembers, saveTeamMember, subscribeToTeamChanges, isAgencyAdmin, isSelf, type TeamMember,
} from '../../services/teamService';
import {
  sampleActivity, sampleAmsPermissions, lastActiveLabel, COMMAND_STATUS_LABEL,
  getRules, saveRule, deleteRule, subscribeToRules, interpretRule, ruleConflicts, ruleNarrows,
  effectiveAccess, permissionLabel, usDate, fetchMonthlyUsage, fetchAgencyMonthlyUsage, fetchAssignedCommands, fetchWeeklyActivity,
  type AdminRule, type MonthlyUsage, type AgencyMonthlyUsage, type AssignedCommand, type WeeklyActivity,
} from '../../services/agencyAdminService';
import '../../styles/Settings.css';
import '../../styles/TeamSettings.css';
import '../../styles/AgencyAdmin.css';

/**
 * Admin Dashboard (agency admins only): what the team is doing, what
 * they're working on and using, and per-person rules on top of the AMS
 * permissions. See docs/admin-dashboard.md. Commands, AI usage and weekly
 * activity come from MyAMP per person (user_id). Last active and AMS
 * permissions are still sample data and rules live in this browser; the page
 * labels both.
 */

const SampleTag: React.FC = () => <span className="aad-tag aad-tag--sample" title="Sample data until the backend is connected">Sample</span>;

/** Your agency's Momentum AMP users (agency-users). */
function useRoster() {
  const [roster, setRoster] = useState<TeamMember[] | null>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    fetchAgencyUsers().then(setRoster).catch(err => { setRoster([]); setError(err?.message || "Couldn't load your agency's users."); });
  }, []);
  return { roster: roster ?? [], loading: roster === null, error };
}

/** Who's switched on for the team (team-members), kept in step with Team access. */
function useTeam() {
  const [team, setTeam] = useState<TeamMember[] | null>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let live = true;
    // force: a fresh team-members call each time the page opens. After a switch is
    // saved, saveTeamMember has already reloaded the list, so the cached copy is current.
    const load = (force = false) => fetchTeamMembers(force)
      .then(t => { if (live) { setTeam(t); setError(''); } })
      .catch(err => { if (live) { setTeam(prev => prev ?? []); setError(err?.message || "Couldn't load your team."); } });
    load(true);
    const unsubscribe = subscribeToTeamChanges(() => load());
    return () => { live = false; unsubscribe(); };
  }, []);
  const enabled = useMemo(() => new Set((team ?? []).map(m => m.id)), [team]);
  return { team, enabled, error };
}

type OpenAssigned = AssignedCommand & { status: NonNullable<AssignedCommand['status']> };
const openOnly = (items: AssignedCommand[]): OpenAssigned[] =>
  items.flatMap(c => (c.status ? [{ ...c, status: c.status }] : []));

/**
 * One person's open commands. userId is their Momentum AMP id (ampUserId, sent as
 * user_id); enabled=false skips the call, e.g. someone who has never signed
 * in — without a user_id the server would answer with the caller's own.
 */
function useAssignedCommands(userId: string | undefined, enabled: boolean) {
  const [commands, setCommands] = useState<OpenAssigned[] | null>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    if (!enabled) return undefined;
    let live = true;
    setCommands(null);
    setError('');
    fetchAssignedCommands(userId)
      .then(r => { if (live) setCommands(openOnly(r.items)); })
      .catch(err => { if (live) { setCommands([]); setError(err?.message || "Couldn't load commands."); } });
    return () => { live = false; };
  }, [userId, enabled]);
  return { commands, error };
}

/** One person's last 7 days. Same userId / enabled rules as useAssignedCommands. */
function useWeeklyActivity(userId: string | undefined, enabled: boolean) {
  const [activity, setActivity] = useState<WeeklyActivity | null>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    if (!enabled) return undefined;
    let live = true;
    setActivity(null);
    setError('');
    fetchWeeklyActivity(userId)
      .then(a => { if (live) setActivity(a); })
      .catch(err => { if (live) setError(err?.message || "Couldn't load activity."); });
    return () => { live = false; };
  }, [userId, enabled]);
  return { activity, error };
}

const monthKey = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

/** month is yyyy-MM; the current month asks for month to date. userId asks for someone else's. */
function useMonthlyUsage(month: string, userId?: string, enabled = true) {
  const [usage, setUsage] = useState<MonthlyUsage | null>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    if (!enabled) return undefined;
    let live = true;
    setUsage(null);
    setError('');
    fetchMonthlyUsage(month === monthKey(new Date()) ? undefined : month, userId)
      .then(u => { if (live) setUsage(u); })
      .catch(err => { if (live) setError(err?.message || "Couldn't load AI usage."); });
    return () => { live = false; };
  }, [month, userId, enabled]);
  return { usage, error };
}

/** Agency-wide usage for a month (yyyy-MM); the current month asks for month to date. */
function useAgencyUsage(month: string) {
  const [usage, setUsage] = useState<AgencyMonthlyUsage | null>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let live = true;
    setUsage(null);
    setError('');
    fetchAgencyMonthlyUsage(month === monthKey(new Date()) ? undefined : month)
      .then(u => { if (live) setUsage(u); })
      .catch(err => { if (live) setError(err?.message || "Couldn't load the agency's AI usage."); });
    return () => { live = false; };
  }, [month]);
  return { usage, error };
}

const pctLabel = (pct: number) => (pct === 0 ? '0' : pct < 1 ? '<1' : String(Math.round(pct)));

function useRulesVersion() {
  const [v, setV] = useState(0);
  useEffect(() => subscribeToRules(() => setV(n => n + 1)), []);
  return v;
}

const amsFor = (m: TeamMember) => sampleAmsPermissions(m, isSelf(m));

// ── Overview ───────────────────────────────────────────────────────────────

const Overview: React.FC = () => {
  const navigate = useNavigate();
  // The main page calls only team-members (Your team), agentlist (Team access) and
  // the agency usage endpoint. agency-users is only called when you open a person.
  const { team, enabled, error } = useTeam();
  const loading = team === null;
  // Your team = exactly the people switched on under Team access (saved per agency).
  // No per-person calls here: commands, activity and usage load when you open a person.
  const members = useMemo(() => team ?? [], [team]);
  const [month, setMonth] = useState(() => monthKey(new Date()));
  const { usage, error: usageError } = useAgencyUsage(month);
  const rulesVersion = useRulesVersion();

  const rows = useMemo(() => members.map(m => {
    const rules = getRules(m.id);
    const ams = amsFor(m);
    const conflicts = rules.filter(r => r.status === 'active' && ruleConflicts(r, ams).length > 0).length;
    return { m, activity: sampleActivity(m), rules, conflicts };
  }).sort((a, b) => Number(isSelf(b.m)) - Number(isSelf(a.m)) || a.m.name.localeCompare(b.m.name)),
  // rulesVersion re-reads the saved rules when they change.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [members, rulesVersion]);

  const activeHour = rows.filter(r => r.activity.lastActiveMinutes >= 0 && r.activity.lastActiveMinutes < 60).length;
  const ruleCount = rows.reduce((n, r) => n + r.rules.length, 0);
  const conflictCount = rows.reduce((n, r) => n + r.conflicts, 0);

  return (
    <>
      <UsageCard usage={usage} error={usageError} month={month} onMonth={setMonth} people={members} />

      <div className="aad-stats glass-surface">
        <div className="aad-stat">
          <span className="aad-stat__label">People</span>
          <span className="aad-stat__value">{loading ? '—' : members.length}</span>
          <span className="aad-stat__hint">Registered in Momentum AMP</span>
        </div>
        <div className="aad-stat">
          <span className="aad-stat__label">Active in the last hour <SampleTag /></span>
          <span className="aad-stat__value">{loading ? '—' : activeHour}</span>
          <span className="aad-stat__hint">Signed in and working</span>
        </div>
        <div className="aad-stat">
          <span className="aad-stat__label">Rules</span>
          <span className="aad-stat__value">{ruleCount}</span>
          <span className={`aad-stat__hint${conflictCount ? ' aad-stat__hint--warn' : ''}`}>
            {conflictCount ? <><FiAlertTriangle size={11} aria-hidden="true" /> {conflictCount} override{conflictCount === 1 ? 's' : ''} the AMS</> : 'No overrides of the AMS'}
          </span>
        </div>
      </div>

      <section className="s2-section">
        <div className="s2-section-label">
          <span className="s2-section-dot s2-section-dot--purple" />
          Your team
        </div>
        {!loading && rows.length === 0 && (
          <p className="aad-note"><FiInfo size={13} aria-hidden="true" />No one is on the team yet. Turn people on under Team access below.</p>
        )}
        {error && <p className="aad-error" role="alert">{error}</p>}

        {loading ? (
          <div className="aad-loading"><FiRefreshCw size={14} className="aad-spin" aria-hidden="true" />Loading your team…</div>
        ) : rows.length === 0 ? null : (
          <div className="aad-table glass-surface" role="table" aria-label="Your team">
            <div className="aad-table__head" role="row">
              <span role="columnheader">Person</span>
              <span role="columnheader">Last active <SampleTag /></span>
              <span role="columnheader">Rules</span>
              <span aria-hidden="true" />
            </div>
            {rows.map(({ m, activity, rules, conflicts }) => {
              const go = () => navigate(`${ROUTES.AGENCY_ADMIN}/${encodeURIComponent(m.id)}`);
              return (
                <div
                  key={m.id}
                  className="aad-table__row"
                  role="row"
                  tabIndex={0}
                  onClick={go}
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); go(); } }}
                  aria-label={`Open ${m.name}`}
                >
                  <span className="aad-person" role="cell">
                    <span className="aad-avatar" aria-hidden="true">{m.initials}</span>
                    <span className="aad-person__who">
                      <span className="aad-person__name">{m.name}{isSelf(m) && <span className="aad-muted"> (you)</span>}</span>
                      <span className="aad-person__email">{m.email}</span>
                    </span>
                  </span>
                  <span role="cell" className="aad-cell" data-label="Last active" title="Sample data until the backend is connected">
                    <span className={`aad-dot${activity.lastActiveMinutes >= 0 && activity.lastActiveMinutes < 60 ? ' aad-dot--on' : ''}`} aria-hidden="true" />
                    {lastActiveLabel(activity.lastActiveMinutes)}
                  </span>
                  <span role="cell" className="aad-cell" data-label="Rules">
                    {rules.length === 0 ? <span className="aad-muted">AMS only</span> : (
                      <>
                        {rules.length} rule{rules.length === 1 ? '' : 's'}
                        {conflicts > 0 && <span className="aad-tag aad-tag--warn"><FiAlertTriangle size={10} aria-hidden="true" />Overrides AMS</span>}
                      </>
                    )}
                  </span>
                  <FiChevronRight className="aad-chevron" size={16} aria-hidden="true" />
                </div>
              );
            })}
          </div>
        )}
      </section>

      <TeamAccess enabled={team === null ? null : enabled} />
    </>
  );
};

// ── Usage meter ────────────────────────────────────────────────────────────
// GET usage/monthly/agency: the agency's share of its allowance, and how the
// agency's usage splits by person and by feature (each split adds up to 100).

const usageLevel = (pct: number) => (pct >= 90 ? 'high' : pct >= 75 ? 'mid' : 'ok');

const UsageCard: React.FC<{
  usage: AgencyMonthlyUsage | null;
  error: string;
  month: string;
  onMonth: (m: string) => void;
  /** Names for by_user (the API's user_name is empty). */
  people: TeamMember[];
}> = ({ usage, error, month, onMonth, people }) => {
  const [view, setView] = useState<'people' | 'items'>('people');
  const now = new Date();
  const thisMonth = monthKey(now);
  const isCurrent = month === thisMonth;
  const [y, m] = month.split('-').map(Number);
  const monthName = new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const daysInMonth = new Date(y, m, 0).getDate();
  const used = usage?.companyPct ?? 0;
  // Straight-line pace from month to date; only meaningful for the current month.
  const projected = isCurrent ? used / (now.getDate() / daysInMonth) : used;
  const level = usageLevel(projected);

  const nameFor = (userId: string, apiName: string) =>
    apiName || people.find(p => p.ampUserId === userId)?.name || `User ${userId}`;
  const shares = !usage ? [] : view === 'people'
    ? usage.byUser.map(u => ({ key: u.userId, label: nameFor(u.userId, u.name), pct: u.pct }))
    : usage.byFeature;
  const top = Math.max(...shares.map(u => u.pct), 1);

  return (
    <section className="aad-usage glass-frost" aria-labelledby="aad-usage-title">
      <div className="aad-usage__head">
        <h2 className="aad-card__title" id="aad-usage-title"><FiActivity size={14} aria-hidden="true" />Monthly AI usage</h2>
        <span className="aad-usage__head-side">
          {isCurrent && <span className="aad-muted aad-usage__reset">Resets {usDate(new Date(y, m, 1))}</span>}
          <input
            type="month"
            className="aad-usage__month"
            value={month}
            max={thisMonth}
            onChange={e => { if (e.target.value) onMonth(e.target.value); }}
            aria-label="Usage month"
          />
        </span>
      </div>
      {error ? <p className="aad-error" role="alert">{error}</p> : !usage ? (
        <div className="aad-loading"><FiRefreshCw size={14} className="aad-spin" aria-hidden="true" />Loading usage…</div>
      ) : (
        <>
          <div className="aad-usage__summary">
            <span className="aad-usage__pct">{pctLabel(used)}%</span>
            <span className="aad-usage__of">of {isCurrent ? 'this month' : monthName}&apos;s AI allowance used by the agency</span>
          </div>
          <div
            className="aad-meter"
            role="meter"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(used)}
            aria-label="Monthly AI allowance used"
          >
            <span className={`aad-meter__fill aad-meter__fill--${usageLevel(used)}`} style={{ width: `${Math.min(100, used)}%` }} />
            {isCurrent && <span className="aad-meter__pace" style={{ left: `${Math.min(100, projected)}%` }} title={`On pace for ${Math.round(projected)}%`} />}
          </div>
          {isCurrent && (
            <p className={`aad-usage__pace aad-usage__pace--${level}`}>
              {level === 'high' && <FiAlertTriangle size={12} aria-hidden="true" />}
              At this pace you&apos;ll use about {Math.round(projected)}% by {usDate(new Date(y, m - 1, daysInMonth))}.
              {projected >= 100 ? ' Agents may pause before the month ends.' : ''}
            </p>
          )}

          <div className="aad-usage__tabs" role="tablist" aria-label="Usage breakdown">
            <button type="button" role="tab" aria-selected={view === 'people'} className={`aad-usage__tab${view === 'people' ? ' aad-usage__tab--on' : ''}`} onClick={() => setView('people')}>By person</button>
            <button type="button" role="tab" aria-selected={view === 'items'} className={`aad-usage__tab${view === 'items' ? ' aad-usage__tab--on' : ''}`} onClick={() => setView('items')}>By feature</button>
          </div>
          {shares.every(u => u.pct === 0) ? <p className="aad-muted">No usage {isCurrent ? 'yet this month' : `in ${monthName}`}.</p> : (
            <ul className="aad-bars" role="tabpanel">
              {shares.map(u => (
                <li key={u.key}>
                  <span className="aad-bars__label" title={u.label}>{u.label}</span>
                  <span className="aad-bars__track"><span className="aad-bars__fill" style={{ width: `${(u.pct / top) * 100}%` }} /></span>
                  <span className="aad-bars__n">{pctLabel(u.pct)}%</span>
                </li>
              ))}
            </ul>
          )}
          <p className="aad-usage__foot aad-muted">Shares are of the agency&apos;s usage this month, not of the allowance.</p>
        </>
      )}
    </section>
  );
};

// ── Team access ────────────────────────────────────────────────────────────

/**
 * Everyone in the AMS (agentlist), so you can switch anyone on, registered in
 * Momentum AMP or not. Each switch saves to team-members.
 */
// Loads agentlist on its own; enabled is null while team-members is still loading.
const TeamAccess: React.FC<{ enabled: Set<string> | null }> = ({ enabled: savedEnabled }) => {
  const [roster, setRoster] = useState<TeamMember[] | null>(null);
  const [error, setError] = useState('');
  // Switches being saved: shown in their new position until the server answers.
  const [pending, setPending] = useState<Record<string, boolean>>({});
  const enabled = useMemo(() => {
    const next = new Set(savedEnabled ?? []);
    Object.entries(pending).forEach(([id, on]) => { if (on) next.add(id); else next.delete(id); });
    return next;
  }, [savedEnabled, pending]);
  const toggle = async (m: TeamMember, on: boolean) => {
    setError('');
    setPending(p => ({ ...p, [m.id]: on }));
    try {
      await saveTeamMember(m, on);
    } catch (err: any) {
      setError(err?.message || "Couldn't save that change.");
    } finally {
      setPending(p => { const { [m.id]: _done, ...rest } = p; return rest; });
    }
  };
  useEffect(() => {
    // force: a fresh agentlist call each time the page opens, not the session's cached copy.
    fetchRoster(true).then(setRoster).catch(err => { setRoster([]); setError(err?.message || "Couldn't load your agency's AMS users."); });
  }, []);
  const [query, setQuery] = useState('');
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (roster ?? [])
      .filter(m => !isSelf(m))
      .filter(m => !q || m.name.toLowerCase().includes(q) || m.email.toLowerCase().includes(q))
      .sort((a, b) => Number(enabled.has(b.id)) - Number(enabled.has(a.id)) || a.name.localeCompare(b.name));
  }, [roster, query, enabled]);
  const others = (roster ?? []).filter(m => !isSelf(m));
  const onCount = others.filter(m => enabled.has(m.id)).length;

  return (
    <section className="s2-section">
      <div className="s2-section-label">
        <span className="s2-section-dot s2-section-dot--purple" />
        Team access · {roster === null || savedEnabled === null ? '…' : `${onCount} of ${others.length} on the team`}
      </div>
      <p className="aad-note">
        <FiInfo size={13} aria-hidden="true" />
        Only people you turn on show under Your team and can be added under Team in the sidebar, messaged, and handed commands. Saved for your whole agency.
      </p>
      <label className="team-set__search">
        <FiSearch size={14} aria-hidden="true" />
        <input type="search" placeholder="Search by name or email" value={query} onChange={e => setQuery(e.target.value)} aria-label="Search your agency's users" />
      </label>
      {error && <p className="aad-error" role="alert">{error}</p>}
      {roster === null ? (
        <div className="aad-loading"><FiRefreshCw size={14} className="aad-spin" aria-hidden="true" />Loading your agency&apos;s AMS users…</div>
      ) : visible.length === 0 ? (
        <p className="aad-empty">{query ? 'No one matches that search.' : 'No other users were found for your agency in the AMS.'}</p>
      ) : (
        <ul className="team-set__list glass-surface">
          {visible.map(m => {
            const on = enabled.has(m.id);
            return (
              <li key={m.id} className="team-set__row">
                <span className="team-set__avatar" aria-hidden="true">{m.initials}</span>
                <span className="team-set__who">
                  <span className="team-set__name">{m.name}</span>
                  <span className="team-set__email">
                    {m.email}
                  </span>
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={on}
                  aria-label={`${m.name} on the team`}
                  className={`team-set__switch${on ? ' team-set__switch--on' : ''}`}
                  disabled={savedEnabled === null || m.id in pending}
                  onClick={() => toggle(m, !on)}
                >
                  <span className="team-set__knob" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

// ── Member detail ──────────────────────────────────────────────────────────

const MemberDetail: React.FC<{ memberId: string }> = ({ memberId }) => {
  const navigate = useNavigate();
  const { roster, loading: rosterLoading } = useRoster();
  const { team } = useTeam();
  const loading = rosterLoading || team === null;
  // Team rows are saved without a Momentum AMP id. If the person has registered, take
  // their agency-users record (matched by AMS agent id, then email) for that id;
  // otherwise show the saved team row on its own.
  const saved = (team ?? []).find(m => m.id === memberId);
  const savedEmail = saved?.email.toLowerCase();
  const target = roster.find(m => m.id === memberId)
    ?? (savedEmail ? roster.find(m => m.email.toLowerCase() === savedEmail) : undefined)
    ?? saved;
  // Their Momentum AMP id goes out as user_id. Someone who has never signed in
  // has none, so nothing is fetched (no user_id would return your own data).
  const targetUserId = target?.ampUserId || undefined;
  const canFetch = !!target && (!!targetUserId || isSelf(target));
  const { commands: mine, error: commandsError } = useAssignedCommands(targetUserId, canFetch);
  const { activity: week, error: weekError } = useWeeklyActivity(targetUserId, canFetch);
  const { usage: rawUsage, error: usageError } = useMonthlyUsage(monthKey(new Date()), targetUserId, canFetch);
  useRulesVersion();
  const [draft, setDraft] = useState('');
  const [preview, setPreview] = useState<ReturnType<typeof interpretRule> | null>(null);

  const member = roster.find(m => m.id === memberId);
  if (loading) return <div className="aad-loading"><FiRefreshCw size={14} className="aad-spin" aria-hidden="true" />Loading…</div>;
  if (!member) return <p className="aad-empty">This person isn&apos;t in your agency&apos;s user list anymore.</p>;

  const first = member.name.split(' ')[0];
  const ams = amsFor(member);
  const rules = getRules(member.id);
  const access = effectiveAccess(ams, rules);
  const activity = sampleActivity(member);
  const self = isSelf(member);
  const previewConflicts = preview ? ruleConflicts(preview, ams) : [];
  // Only shown if the server answered for this person.
  const usage = rawUsage && (self || (!!member.ampUserId && rawUsage.userId === member.ampUserId)) ? rawUsage : null;
  const topItem = Math.max(...(usage?.byItem ?? []).map(u => u.pct), 0.1);

  const check = () => { if (draft.trim()) setPreview(interpretRule(draft)); };
  const confirm = () => {
    if (!preview) return;
    saveRule(member.id, { ...preview, id: `rule-${Date.now()}`, status: 'active', createdAt: new Date().toISOString() });
    setPreview(null);
    setDraft('');
  };

  return (
    <>
      <div className="aad-member">
        <span className="aad-avatar aad-avatar--lg" aria-hidden="true">{member.initials}</span>
        <div>
          <h2 className="aad-member__name">{member.name}{isSelf(member) && <span className="aad-muted"> (you)</span>}</h2>
          <p className="aad-member__meta">
            {member.email} · <span className={`aad-dot${activity.lastActiveMinutes >= 0 && activity.lastActiveMinutes < 60 ? ' aad-dot--on' : ''}`} aria-hidden="true" />
            {lastActiveLabel(activity.lastActiveMinutes)} <SampleTag />
          </p>
        </div>
      </div>

      <div className="aad-grid">
        <div className="aad-col">
          <section className="aad-card glass-surface">
            <h3 className="aad-card__title"><FiClipboard size={14} aria-hidden="true" />Working on</h3>
            {!canFetch ? <p className="aad-muted">{first} hasn&apos;t signed in to Momentum AMP yet.</p>
              : commandsError ? <p className="aad-error" role="alert">{commandsError}</p>
              : mine === null ? <p className="aad-muted">Loading commands…</p>
              : mine.length === 0 ? <p className="aad-muted">No open commands assigned to {first}.</p>
              : (
                <ul className="aad-list">
                  {mine.slice(0, 10).map(c => (
                    <li key={c.id}>
                      <button type="button" className="aad-command" onClick={() => navigate(`/momentum-kanban/${c.id}`)}>
                        <span className="aad-command__code">{c.code}</span>
                        <span className="aad-command__title">
                          {c.title || 'Untitled command'}
                          {c.agentName && <span className="aad-muted"> · {c.agentName}</span>}
                        </span>
                        <span className={`aad-status aad-status--${c.status}`}>{COMMAND_STATUS_LABEL[c.status]}</span>
                      </button>
                    </li>
                  ))}
                  {mine.length > 10 && <li className="aad-muted">+{mine.length - 10} more in Command Center</li>}
                </ul>
              )}
          </section>

          <section className="aad-card glass-surface">
            <h3 className="aad-card__title"><FiActivity size={14} aria-hidden="true" />AI usage this month</h3>
            {!canFetch ? <p className="aad-muted">{first} hasn&apos;t signed in to Momentum AMP yet.</p>
              : usageError ? <p className="aad-error" role="alert">{usageError}</p>
              : !rawUsage ? <p className="aad-muted">Loading usage…</p>
              : !usage ? <p className="aad-error" role="alert">The server returned usage for a different person. Please refresh.</p>
              : (
                <>
                  <p className="aad-usage__member">
                    <strong>{pctLabel(usage.userPct)}%</strong> of the agency&apos;s monthly allowance
                    <span className="aad-muted"> · agency total {pctLabel(usage.companyPct)}%</span>
                  </p>
                  {usage.byItem.some(u => u.pct > 0) && <p className="aad-subhead">Split of {self ? 'your' : `${first}'s`} use</p>}
                  <ul className="aad-bars">
                    {usage.byItem.filter(u => u.pct > 0).map(u => (
                      <li key={u.key}>
                        <span className="aad-bars__label">{u.label}</span>
                        <span className="aad-bars__track"><span className="aad-bars__fill" style={{ width: `${(u.pct / topItem) * 100}%` }} /></span>
                        <span className="aad-bars__n">{pctLabel(u.pct)}%</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
          </section>

          <section className="aad-card glass-surface">
            <h3 className="aad-card__title">Activity · last 7 days</h3>
            {!canFetch ? <p className="aad-muted">{first} hasn&apos;t signed in to Momentum AMP yet.</p>
              : weekError ? <p className="aad-error" role="alert">{weekError}</p>
              : !week ? <p className="aad-muted">Loading activity…</p>
              : (
                <>
                  <div className="aad-kv">
                    <span>Chats with Digital Agents</span><strong>{week.chats}</strong>
                    <span>Routine runs</span><strong>{week.routineRuns}</strong>
                    <span>Commands</span><strong>{week.tasks}</strong>
                  </div>
                  <p className="aad-subhead">Digital Agents used</p>
                  {week.agentsUsed.length === 0 ? <p className="aad-muted">None this week.</p> : (
                    <ul className="aad-bars">
                      {week.agentsUsed.map(a => (
                        <li key={a.name}>
                          <span className="aad-bars__label" title={a.name}>{a.name}</span>
                          <span className="aad-bars__track"><span className="aad-bars__fill" style={{ width: `${(a.count / Math.max(week.agentsUsed[0].count, 1)) * 100}%` }} /></span>
                          <span className="aad-bars__n">{a.count}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  <p className="aad-subhead">Chat topics</p>
                  {week.topTopics.length === 0 ? <p className="aad-muted">No chats this week.</p> : (
                    <div className="aad-chips">
                      {week.topTopics.map(t => (
                        <span key={t.id} className="aad-chip" title={t.title}>
                          {t.title.length > 48 ? `${t.title.slice(0, 47)}…` : t.title}
                        </span>
                      ))}
                    </div>
                  )}
                  {!self && <p className="aad-privacy"><FiShield size={12} aria-hidden="true" />You see chat titles and counts only. {first}&apos;s messages stay private.</p>}
                </>
              )}
          </section>
        </div>

        <div className="aad-col">
          <section className="aad-card glass-surface">
            <h3 className="aad-card__title">Rules for {first}</h3>
            <p className="aad-muted aad-card__lead">
              Type a rule the way you&apos;d say it, e.g. &ldquo;Never let them see commissions.&rdquo; Rules sit on top of {first}&apos;s AMS permissions and apply to their Digital Agents too.
            </p>
            <div className="aad-rule-input">
              <textarea
                rows={2}
                value={draft}
                onChange={e => { setDraft(e.target.value); setPreview(null); }}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); check(); } }}
                placeholder={`A rule for ${first}…`}
                aria-label={`New rule for ${member.name}`}
              />
              <button type="button" className="aad-btn aad-btn--primary" onClick={check} disabled={!draft.trim()}>Check rule</button>
            </div>

            {preview && (
              <div className={`aad-preview${previewConflicts.length ? ' aad-preview--warn' : ''}`} role="status">
                <p className="aad-preview__line">
                  {preview.targets.length ? (
                    <><span className={`aad-tag aad-tag--${preview.effect}`}>{preview.effect === 'grant' ? 'Allow' : 'Restrict'}</span>{preview.targets.map(permissionLabel).join(' · ')}</>
                  ) : (
                    <><span className="aad-tag aad-tag--agent">Agent instruction</span>{first}&apos;s Digital Agents will follow this. It doesn&apos;t change a permission.</>
                  )}
                </p>
                {previewConflicts.length > 0 && (
                  <p className="aad-preview__warn">
                    <FiAlertTriangle size={13} aria-hidden="true" />
                    This gives {first} access the AMS doesn&apos;t allow ({previewConflicts.map(permissionLabel).join(', ')}). It will override the AMS for {first}.
                  </p>
                )}
                <div className="aad-preview__actions">
                  <button type="button" className="aad-btn" onClick={() => setPreview(null)}><FiX size={13} aria-hidden="true" />Edit</button>
                  <button type="button" className={`aad-btn ${previewConflicts.length ? 'aad-btn--warn' : 'aad-btn--primary'}`} onClick={confirm}>
                    <FiCheck size={13} aria-hidden="true" />{previewConflicts.length ? 'Save and override AMS' : 'Save rule'}
                  </button>
                </div>
              </div>
            )}

            {rules.length === 0 ? <p className="aad-muted">No rules yet. {first} has their AMS permissions only.</p> : (
              <ul className="aad-rules">
                {rules.map(r => <RuleRow key={r.id} rule={r} memberId={member.id} ams={ams} />)}
              </ul>
            )}
          </section>

          <section className="aad-card glass-surface">
            <h3 className="aad-card__title">What {first} can do</h3>
            <div className="aad-access" role="table" aria-label={`${member.name}'s access`}>
              <div className="aad-access__head" role="row">
                <span role="columnheader">Permission</span>
                <span role="columnheader">AMS <SampleTag /></span>
                <span role="columnheader">With rules</span>
              </div>
              {access.map(a => (
                <div key={a.key} className={`aad-access__row${a.source === 'rule_overrides_ams' ? ' aad-access__row--warn' : ''}`} role="row">
                  <span role="cell">{a.label}</span>
                  <span role="cell"><Allowed on={ams[a.key] !== false} /></span>
                  <span role="cell" className="aad-access__eff">
                    <Allowed on={a.allowed} />
                    {a.source === 'rule' && <span className="aad-tag" title={a.ruleText}>Rule</span>}
                    {a.source === 'rule_overrides_ams' && <span className="aad-tag aad-tag--warn" title={a.ruleText}><FiAlertTriangle size={10} aria-hidden="true" />Overrides AMS</span>}
                  </span>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

const Allowed: React.FC<{ on: boolean }> = ({ on }) => (
  <span className={`aad-allowed${on ? ' aad-allowed--on' : ''}`}>
    {on ? <FiCheck size={12} aria-hidden="true" /> : <FiX size={12} aria-hidden="true" />}{on ? 'Allowed' : 'Not allowed'}
  </span>
);

const RuleRow: React.FC<{ rule: AdminRule; memberId: string; ams: Record<string, boolean> }> = ({ rule, memberId, ams }) => {
  const conflicts = ruleConflicts(rule, ams);
  const narrows = ruleNarrows(rule, ams);
  const paused = rule.status === 'paused';
  return (
    <li className={`aad-rule${paused ? ' aad-rule--paused' : ''}${conflicts.length && !paused ? ' aad-rule--warn' : ''}`}>
      <div className="aad-rule__body">
        <p className="aad-rule__text">&ldquo;{rule.text}&rdquo;</p>
        <p className="aad-rule__meta">
          {rule.targets.length ? (
            <><span className={`aad-tag aad-tag--${rule.effect}`}>{rule.effect === 'grant' ? 'Allow' : 'Restrict'}</span>{rule.targets.map(permissionLabel).join(' · ')}</>
          ) : (
            <><span className="aad-tag aad-tag--agent">Agent instruction</span>Doesn&apos;t change a permission</>
          )}
        </p>
        <p className="aad-rule__badges">
          {conflicts.length > 0 && <span className="aad-tag aad-tag--warn"><FiAlertTriangle size={10} aria-hidden="true" />Overrides AMS: {conflicts.map(permissionLabel).join(', ')}</span>}
          {narrows.length > 0 && <span className="aad-tag">Narrows AMS</span>}
          {rule.appliesToAgents && <span className="aad-tag">Applies to Digital Agents</span>}
          {paused && <span className="aad-tag">Paused</span>}
        </p>
      </div>
      <div className="aad-rule__actions">
        <button type="button" className="aad-icon-btn" onClick={() => saveRule(memberId, { ...rule, status: paused ? 'active' : 'paused' })} aria-label={paused ? 'Resume rule' : 'Pause rule'} title={paused ? 'Resume' : 'Pause'}>
          {paused ? <FiPlay size={13} /> : <FiPause size={13} />}
        </button>
        <button type="button" className="aad-icon-btn aad-icon-btn--danger" onClick={() => deleteRule(memberId, rule.id)} aria-label="Delete rule" title="Delete">
          <FiTrash2 size={13} />
        </button>
      </div>
    </li>
  );
};

// ── Page ───────────────────────────────────────────────────────────────────

const AgencyAdminPage: React.FC = () => {
  const navigate = useNavigate();
  // Routed as /admin-dashboard/*, so the person's id is the splat.
  const params = useParams();
  const memberId = (params.memberId ?? params['*'] ?? '').split('/')[0] || undefined;
  const admin = isAgencyAdmin();

  return (
    <div className="s2-page">
      <motion.div className="s2-container" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
        <section className="s2-top-header">
          <div className="s2-top-header__text">
            <h1 className="s2-top-header__title">Admin Dashboard</h1>
            <p className="s2-top-header__subtitle">
              See what your agency is doing: who&apos;s working on what, what they use, and what they&apos;re allowed to do.
            </p>
          </div>
          {memberId && (
            <div className="s2-top-header__actions">
              <button className="s2-top-header__nav-btn" onClick={() => navigate(ROUTES.AGENCY_ADMIN)}>
                <FiArrowLeft size={14} />
                <span>Back to team</span>
              </button>
            </div>
          )}
        </section>

        {!admin ? (
          <p className="aad-empty">Only agency admins can open the Admin Dashboard.</p>
        ) : (
          <>
            <p className="aad-note aad-note--preview">
              <FiInfo size={13} aria-hidden="true" />
              Preview. Commands, AI usage and activity are live; anything marked <SampleTag /> is sample data until it&apos;s connected. Rules are saved in this browser and aren&apos;t applied to your team yet.
            </p>
            {memberId ? <MemberDetail memberId={decodeURIComponent(memberId)} /> : <Overview />}
          </>
        )}
      </motion.div>
    </div>
  );
};

export default AgencyAdminPage;
